import { create } from 'zustand';

/**
 * Tracks whether the cloud → Spire bridge is reachable, driven by the API
 * request layer: any successful call marks it online; a network failure or a
 * 502/503/504 (the broker can't reach Spire) marks it offline. Powers the
 * "Spire bridge offline" banner so a rep in a low-signal clinic gets a clear
 * signal instead of silent failures.
 */
/**
 * Why the bridge is down, when the API told us:
 *   - 'unreachable': network failure / tunnel down / breaker open
 *   - 'auth':        Spire is rejecting the app's service account (K-Dental IT
 *                    disabled it). Nothing a rep can do; tell them plainly.
 */
export type BridgeDownReason = 'unreachable' | 'auth';

interface ConnectivityState {
  bridgeOnline: boolean;
  reason: BridgeDownReason | null;
  markOnline: () => void;
  markOffline: (reason: BridgeDownReason) => void;
}

export const useConnectivity = create<ConnectivityState>((set) => ({
  bridgeOnline: true,
  reason: null,
  markOnline: () => set({ bridgeOnline: true, reason: null }),
  markOffline: (reason) => set({ bridgeOnline: false, reason }),
}));

export function reportOk(): void {
  if (!useConnectivity.getState().bridgeOnline) useConnectivity.getState().markOnline();
}
export function reportBridgeDown(reason: BridgeDownReason = 'unreachable'): void {
  const st = useConnectivity.getState();
  if (st.bridgeOnline || st.reason !== reason) st.markOffline(reason);
}
